'use client';

import { useRef } from 'react';
import {
  IconBuildingStore,
  IconMessageCircleQuestion,
} from '@tabler/icons-react';
import {
  Box,
  Center,
  rgba,
  ScrollArea,
  Stack,
  Tabs,
  useMantineTheme,
} from '@mantine/core';
import { ChatConversationEmptyState } from '@/src/shared/components/chat/chat-conversation-empty-state';
import { ChatConversationListHeader } from '@/src/shared/components/chat/chat-conversation-list-header';
import { ChatConversationTileList } from '@/src/shared/components/chat/chat-conversation-tile-list';
import ChatConversationTileListSkeleton from '@/src/shared/components/chat/loading/chat-conversation-tile-list-skeleton';
import { useChatConversationListScroll } from '@/src/shared/hooks/use-chat-conversation-list-scroll';
import { useMarketplaceCustomerChatContext } from '../../../_contexts';
import { EChatTab, TChatTab } from '../../../_enums/chat-tab';
import { ChatFloatingWindowComplaintConversationTile } from '../../ui/chat-popup/chat-floating-window-complaint-conversation-tile';
import { ChatFloatingWindowMedicationConversationTile } from './chat-floating-window-self-medication-conversation-tile';

export function ChatFloatingWindowConversationMenu() {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const {
    selectedConversationId,
    setSelectedConversationId,
    refreshConversations,
    isGettingConversations,
    isGettingMoreConversations,
    isRefreshingConversations,
    isReachingEndConversations,
    loadMoreConversations,
    sellerConversations,
    currentOpenedTab,
    setCurrentOpenedTab,
  } = useMarketplaceCustomerChatContext();
  const { handleScrollReachBottom } = useChatConversationListScroll({
    scrollAreaRef: viewportRef,
    isLoadingMore: isGettingMoreConversations,
    isRefreshing: isRefreshingConversations,
    conversations: sellerConversations,
    onScrollToBottom: loadMoreConversations,
    isReachingEnd: isReachingEndConversations,
    refreshConversations,
  });

  const theme = useMantineTheme();

  return (
    <Stack w="18rem" className="border-r border-r-gray-200" gap={0} h="100%">
      <ChatConversationListHeader
        size="sm"
        onRefresh={refreshConversations}
        isRefreshing={isRefreshingConversations}
      />

      <Tabs
        orientation="vertical"
        h="100%"
        value={currentOpenedTab}
        onChange={(value) => {
          setCurrentOpenedTab((value as TChatTab) ?? EChatTab.WithSeller);
        }}
      >
        <Tabs.List ml="0.3rem">
          <Tabs.Tab
            value={EChatTab.WithSeller}
            aria-label="Tab chat dengan penjual"
            fz="xs"
            bg={
              currentOpenedTab === EChatTab.WithSeller
                ? rgba(theme.colors.brand[0], 0.2)
                : undefined
            }
            c={currentOpenedTab === EChatTab.WithSeller ? 'brand.8' : 'gray.7'}
            classNames={{
              tabLabel: 'text-left!',
            }}
          >
            <IconBuildingStore size={18} />
          </Tabs.Tab>
          <Tabs.Tab
            value={EChatTab.Support}
            aria-label="Tab bantuan"
            fz="xs"
            bg={
              currentOpenedTab === EChatTab.Support
                ? rgba(theme.colors.brand[0], 0.2)
                : undefined
            }
            c={currentOpenedTab === EChatTab.Support ? 'brand.8' : 'gray.7'}
            classNames={{
              tabLabel: 'text-left!',
            }}
          >
            <IconMessageCircleQuestion size={18} />
          </Tabs.Tab>
        </Tabs.List>

        <ScrollArea.Autosize
          viewportRef={viewportRef}
          onScrollPositionChange={handleScrollReachBottom}
          scrollbarSize={6}
          classNames={{
            root: 'h-full',
            content: 'h-full',
          }}
        >
          <Box ml="0.4rem" mr="0.6rem" mb="0.3rem" h="100%">
            {!isGettingConversations && !isRefreshingConversations ? (
              <>
                <Tabs.Panel value={EChatTab.WithSeller} h="100%">
                  {sellerConversations.length > 0 ? (
                    <ChatConversationTileList
                      size="sm"
                      conversations={sellerConversations}
                      selectedConversationId={selectedConversationId}
                      setSelectedConversationId={setSelectedConversationId}
                      showLastMessages={false}
                      isLoadingMore={isGettingMoreConversations}
                    />
                  ) : (
                    <Center h="100%" mt="-1rem">
                      <ChatConversationEmptyState size="sm" />
                    </Center>
                  )}
                </Tabs.Panel>
                <Tabs.Panel value={EChatTab.Support}>
                  <ChatFloatingWindowMedicationConversationTile />
                  <ChatFloatingWindowComplaintConversationTile />
                </Tabs.Panel>
              </>
            ) : (
              <Box mt="0.5rem">
                <ChatConversationTileListSkeleton
                  size="sm"
                  showLastMessages={false}
                />
              </Box>
            )}
          </Box>
        </ScrollArea.Autosize>
      </Tabs>
    </Stack>
  );
}
